import type { GetLeadResult } from '@whinerp/client';
import { Icon } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';

/**
 * Where a lead appears to have come from (F-152).
 *
 * The specification's own warning is the whole design: "state plainly that this
 * is an estimate from IP. City-level accuracy is poor and a rep who treats it
 * as fact will be wrong in front of a customer."
 *
 * So the chip never shows a place on its own. It shows the place and what
 * produced it in the same breath - and when that was a sandbox estimate rather
 * than a lookup, it says sandbox. A chip reading "Dubai" with the provenance
 * behind a hover is a chip that gets read aloud on a call.
 *
 * Three states, and they are not the same state:
 *   a place           - resolved, with its source named
 *   a private address - nothing to look up, and never will be
 *   nothing           - not drawn at all, rather than an empty chip
 */
export function LocationChip({
  location,
}: {
  location: GetLeadResult['location'];
}): JSX.Element | null {
  const t = useT();
  if (!location) return null;

  if (location.source === 'private') {
    return (
      <span
        data-lead-location="private"
        className="inline-flex items-center gap-1 rounded-control bg-sunken px-2 py-0.5 text-meta text-ink-meta"
      >
        <Icon name="MapPin" size="inline" decorative />
        {t('geo.private')}
      </span>
    );
  }

  const place = [location.city, location.region, location.country]
    .filter((part): part is string => Boolean(part))
    .join(', ');
  /*
   * Belt and braces, and named as such: the server already returns null when
   * nothing resolved, so a test cannot reach this line by way of the product.
   * What the "no chip on an unresolved lead" test actually holds is the server
   * - mutating THAT to return a location regardless kills it.
   */
  if (place === '') return null;

  return (
    <span
      data-lead-location={location.source}
      className="inline-flex flex-wrap items-center gap-1 rounded-control bg-sunken px-2 py-0.5 text-meta text-ink-600"
    >
      <Icon name="MapPin" size="inline" decorative />
      <span data-lead-place>{place}</span>
      {/* Beside the place, not behind a hover: the caveat has to be readable by
          somebody glancing at the screen while they are talking. */}
      <span data-lead-location-source className="text-ink-meta">
        {location.source === 'sandbox'
          ? t('geo.sandbox')
          : location.source === 'unrecorded'
            ? t('geo.unrecorded')
            : t('geo.estimate', { values: { source: location.source } })}
      </span>
    </span>
  );
}
